/**
 * ServicePackagesPanel — D4/E11. Mounted in BOTH the CS and Sales command
 * centers (same component + same endpoints = synchronized tabs). Contains:
 *  1. The 6 multifamily packages with editable prices (live-wired to the
 *     public website package display + quote engine).
 *  2. New Quote Submissions log — new rows in red, clear on review, no delete.
 */
import { useCallback, useEffect, useState } from 'react';
import { Package, RefreshCw, Save, CheckCircle2, Mail, Phone as PhoneIcon, FileText } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';
import BuildProposalDialog from './BuildProposalDialog';

const GOLD = '#C9A962';

export default function ServicePackagesPanel({ dept = 'cs' }) {
  const [tab, setTab] = useState('packages');
  const [packages, setPackages] = useState([]);
  const [edits, setEdits] = useState({});
  const [subs, setSubs] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [nudge, setNudge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [na, setNa] = useState(null);
  const [naFilter, setNaFilter] = useState({ kind: 'all', date_from: '', date_to: '' });
  const [proposalQuote, setProposalQuote] = useState(null);

  const loadAnalytics = useCallback(async (f) => {
    try {
      const qs = new URLSearchParams({ kind: f.kind, date_from: f.date_from, date_to: f.date_to });
      setNa(await apiFetch(`/api/service-packages/nudge-analytics?${qs}`));
    } catch (_) {}
  }, []);

  const load = useCallback(async () => {
    try {
      const [pk, qs, ns] = await Promise.all([
        apiFetch('/api/service-packages/admin'),
        apiFetch('/api/service-packages/quotes'),
        apiFetch('/api/service-packages/nudge-settings'),
      ]);
      setPackages(pk.packages || []);
      setSubs(qs.submissions || []);
      setNewCount(qs.new_count || 0);
      setNudge(ns || null);
    } catch (_) { /* non-admin viewers just see nothing */ }
    setLoading(false);
  }, []);

  const toggleNudge = async (key) => {
    const next = { ...nudge, [key]: !nudge[key] };
    setNudge(next);
    try {
      await apiFetch('/api/service-packages/nudge-settings', {
        method: 'PUT',
        body: { multifamily_enabled: !!next.multifamily_enabled, commercial_enabled: !!next.commercial_enabled },
      });
      toast.success('Follow-up nudge settings saved');
    } catch (e) { toast.error(e.detail || 'Save failed'); load(); }
  };

  useEffect(() => {
    load();
    loadAnalytics(naFilter);
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, loadAnalytics]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePrice = async (key) => {
    const val = parseFloat(edits[key]);
    if (!val || val <= 0) { toast.error('Enter a valid price'); return; }
    try {
      await apiFetch(`/api/service-packages/admin/${key}`, { method: 'PUT', body: { monthly_price: val } });
      toast.success('Price updated — live on the public website now');
      setEdits((e) => { const n = { ...e }; delete n[key]; return n; });
      load();
    } catch (err) { toast.error(err.detail || 'Update failed'); }
  };

  const markReviewed = async (sid) => {
    try {
      await apiFetch(`/api/service-packages/quotes/${sid}/review`, { method: 'POST' });
      toast.success('Marked reviewed');
      load();
    } catch (err) { toast.error(err.detail || 'Action failed'); }
  };

  if (loading) return null;
  if (!packages.length && !subs.length) return null;

  return (
    <section className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-5" data-testid={`service-packages-panel-${dept}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <Package className="h-4 w-4" /> Service Packages · Multifamily
        </h2>
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <button onClick={() => setTab('packages')} data-testid="sp-tab-packages"
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${tab === 'packages' ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`}>
            Packages
          </button>
          <button onClick={() => setTab('quotes')} data-testid="sp-tab-quotes"
            className={`relative px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${tab === 'quotes' ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`}>
            New Quote Submissions
            {newCount > 0 && (
              <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center" data-testid="sp-new-quotes-badge">{newCount}</span>
            )}
          </button>
          <button onClick={() => setTab('nudge')} data-testid="sp-tab-nudge"
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${tab === 'nudge' ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`}>
            Nudge Analytics
          </button>
          <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300 hover:text-white" data-testid="sp-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {tab === 'packages' && (
        <div className="overflow-x-auto">
          <p className="text-xs text-zinc-400 mb-3">
            Prices below drive the public website package display and the Get-a-Quote estimate ranges in real time.
          </p>
          <table className="admin-stack-table text-sm" data-testid={`service-packages-table-${dept}`}>
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b border-zinc-700">
                <th className="py-2 pr-3">Package</th>
                <th className="py-2 pr-3">Price / unit / mo</th>
                <th className="py-2 pr-3">12 mo range</th>
                <th className="py-2 pr-3">18 mo</th>
                <th className="py-2 pr-3">24 mo</th>
                <th className="py-2 pr-3">36 mo rate</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => {
                const tr = p.term_ranges || {};
                const fmt = (r) => r ? (r.low === r.high ? `$${r.low}` : `$${r.low}–$${r.high}`) : '—';
                return (
                  <tr key={p.key} className="border-b border-zinc-800" data-testid={`sp-row-${p.key}`}>
                    <td className="py-2.5 pr-3 font-semibold text-white" data-label="Package">{p.name}</td>
                    <td className="py-2.5 pr-3" data-label="Price / unit / month">
                      <div className="flex items-center gap-1">
                        <span style={{ color: GOLD }}>$</span>
                        <input
                          type="number" min="1" step="1"
                          value={edits[p.key] ?? p.monthly_price}
                          onChange={(e) => setEdits((s) => ({ ...s, [p.key]: e.target.value }))}
                          className="w-20 rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white"
                          data-testid={`sp-price-input-${p.key}`}
                        />
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-zinc-300" data-label="12 months">{fmt(tr['12'])}</td>
                    <td className="py-2.5 pr-3 text-zinc-300" data-label="18 months">{fmt(tr['18'])}</td>
                    <td className="py-2.5 pr-3 text-zinc-300" data-label="24 months">{fmt(tr['24'])}</td>
                    <td className="py-2.5 pr-3 text-zinc-300" data-label="36 months">{fmt(tr['36'])}</td>
                    <td className="py-2.5 text-right" data-label="Actions">
                      {edits[p.key] !== undefined && parseFloat(edits[p.key]) !== p.monthly_price && (
                        <button onClick={() => savePrice(p.key)}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-[#C9A962] text-black"
                          data-testid={`sp-save-price-${p.key}`}>
                          <Save className="h-3 w-3" /> Save
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'quotes' && (
        <div className="space-y-3" data-testid="sp-quotes-log">
          {nudge && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-700 bg-zinc-800/40 p-3" data-testid="sp-nudge-settings">
              <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">48h Follow-Up Nudge</span>
              {[['multifamily_enabled', 'Multifamily'], ['commercial_enabled', 'Commercial']].map(([k, l]) => (
                <button key={k} onClick={() => toggleNudge(k)}
                  className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${nudge[k] ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500' : 'text-zinc-500 border-zinc-600'}`}
                  data-testid={`sp-nudge-toggle-${k}`}>
                  {l}: {nudge[k] ? 'ON' : 'OFF'}
                </button>
              ))}
              <span className="text-[10px] text-zinc-500">Sends a warm reminder email 48h after a quote if no Sales outreach has been logged.</span>
            </div>
          )}
          {subs.length === 0 && <p className="text-sm text-zinc-500">No quote submissions yet.</p>}
          {subs.map((s) => {
            const isNew = s.status === 'new';
            return (
              <div key={s.submission_id}
                className={`rounded-lg border p-3 ${isNew ? 'border-red-500 bg-red-950/30' : 'border-zinc-700 bg-zinc-800/40'}`}
                data-testid={`sp-quote-row-${s.submission_id}`} data-status={s.status}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className={`font-bold ${isNew ? 'text-red-300' : 'text-white'}`}>
                      {s.full_name} · {s.property_name}
                      {isNew && <span className="ml-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black uppercase">New Quote Submission</span>}
                      {s.below_minimum_flag && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-700 text-white text-[10px] font-black uppercase" data-testid={`sp-below-min-${s.submission_id}`}>{s.below_minimum_flag}</span>}
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">{s.property_address} · {s.unit_count} units</p>
                    <p className="text-xs text-zinc-400 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span>
                      <span className="inline-flex items-center gap-1"><PhoneIcon className="h-3 w-3" />{s.phone}</span>
                      <span>Prefers: {s.preferred_contact}</span>
                    </p>
                    <p className="text-xs mt-1" style={{ color: GOLD }}>
                      {s.package_name} · {s.contract_term} months
                      {s.estimate_range ? ` · shown $${s.estimate_range.low}–$${s.estimate_range.high}${s.kind === 'commercial' ? '/mo' : '/unit/mo'}` : ' · no price shown (review required)'}
                    </p>
                    {s.internal_breakdown && (
                      <details className="mt-2 text-xs" data-testid={`sp-internal-breakdown-${s.submission_id}`}>
                        <summary className="cursor-pointer font-bold uppercase tracking-wider text-zinc-400">Internal cost breakdown (NECS only)</summary>
                        <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-zinc-300">
                          <span>Direct Labor: ${Number(s.internal_breakdown.direct_labor).toLocaleString()}</span>
                          <span>Supplies: ${Number(s.internal_breakdown.supplies).toLocaleString()}</span>
                          <span>Equipment: ${Number(s.internal_breakdown.equipment).toLocaleString()}</span>
                          <span>Transportation: ${Number(s.internal_breakdown.transportation).toLocaleString()}{s.internal_breakdown.route_density_applied ? ' (route density)' : ''}</span>
                          <span>Overhead: ${Number(s.internal_breakdown.overhead_allocation).toLocaleString()}</span>
                          <span>Total Cost: ${Number(s.internal_breakdown.total_estimated_cost).toLocaleString()}</span>
                          <span>Target Margin: {s.internal_breakdown.target_gross_margin_pct}%</span>
                          <span>Rec. Price: ${Number(s.internal_breakdown.recommended_contract_price).toLocaleString()}</span>
                        </div>
                      </details>
                    )}
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      Submitted {new Date(s.submitted_at).toLocaleString()}
                      {s.reviewed_by && ` · acknowledged by ${s.reviewed_by}`}
                      {s.requested_final_quote && <span className="ml-2 font-bold" style={{ color: '#C9A962' }}>Final quote requested</span>}
                      {s.receipt_email_sent_at && <span className="ml-2 text-emerald-500">Receipt emailed</span>}
                      {s.followup_sent_at && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-400 text-[10px] font-bold uppercase" data-testid={`sp-followup-chip-${s.submission_id}`}>
                          Follow-up sent {new Date(s.followup_sent_at).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                    {Array.isArray(s.proposal_log) && s.proposal_log.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`sp-proposal-log-${s.submission_id}`}>
                        {s.proposal_log.map((pl, i) => (
                          <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${pl.action === 'sent' ? 'bg-purple-900/50 text-purple-300' : 'bg-zinc-800 text-zinc-400'}`}>
                            Proposal {pl.action} by {pl.by} · {new Date(pl.at).toLocaleString()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {isNew && (
                      <button onClick={() => markReviewed(s.submission_id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[#C9A962] text-black"
                        data-testid={`sp-review-btn-${s.submission_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Acknowledge
                      </button>
                    )}
                    {s.kind === 'commercial' && s.status === 'reviewed' && (
                      <button onClick={() => setProposalQuote(s)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
                        title="Build a Stage 3 proposal pre-populated from this quote"
                        data-testid={`sp-build-proposal-btn-${s.submission_id}`}>
                        <FileText className="h-3.5 w-3.5" /> Build Proposal
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {tab === 'nudge' && (
        <div data-testid="sp-nudge-analytics">
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Quote Type</p>
              <select value={naFilter.kind}
                onChange={(e) => { const f = { ...naFilter, kind: e.target.value }; setNaFilter(f); loadAnalytics(f); }}
                className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1.5 text-white text-xs"
                data-testid="sp-na-kind">
                <option value="all">All</option>
                <option value="multifamily">Multifamily</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">From</p>
              <input type="date" value={naFilter.date_from}
                onChange={(e) => { const f = { ...naFilter, date_from: e.target.value }; setNaFilter(f); loadAnalytics(f); }}
                className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" data-testid="sp-na-from" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">To</p>
              <input type="date" value={naFilter.date_to}
                onChange={(e) => { const f = { ...naFilter, date_to: e.target.value }; setNaFilter(f); loadAnalytics(f); }}
                className="rounded bg-zinc-800 border border-zinc-600 px-2 py-1 text-white text-xs" data-testid="sp-na-to" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              ['Nudges Sent', na?.sent ?? 0, 'sp-na-sent'],
              ['Recovered (reviewed after nudge)', na?.recovered ?? 0, 'sp-na-recovered'],
              ['Conversion Rate', `${na?.conversion_rate ?? 0}%`, 'sp-na-rate'],
            ].map(([label, val, tid]) => (
              <div key={tid} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-4 text-center" data-testid={tid}>
                <p className="text-3xl font-black" style={{ color: GOLD, fontFamily: 'Barlow Condensed, sans-serif' }}>{val}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-600 mt-3">A lead counts as recovered when its quote is marked reviewed after the follow-up email went out.</p>
        </div>
      )}
      <BuildProposalDialog
        quote={proposalQuote}
        open={!!proposalQuote}
        onClose={() => setProposalQuote(null)}
        onDone={() => load()}
      />
    </section>
  );
}
